import { useEffect, useRef, useState } from 'react';
import { Control, useController, UseFormSetValue } from 'react-hook-form';

import { analyzeDocxMergeFields } from '../../templates/utils/analyze-docx-merge-fields.util';
import {
  getQuickSignStepFourFile,
  QuickSignFormSchema,
  QuickSignSignatureTab,
} from '../validation/quick-sign.validation';

type UseQuickSignSignatureSettingsParams = {
  control: Control<QuickSignFormSchema>;
  isOpen: boolean;
  setValue: UseFormSetValue<QuickSignFormSchema>;
};

const buildTabsFromAnchors = (
  anchors: string[],
  existingTabs: QuickSignSignatureTab[],
): QuickSignSignatureTab[] => {
  const tabsByAnchor = new Map(existingTabs.map((tab) => [tab.anchorString, tab]));
  return anchors.map((anchor) => {
    const existing = tabsByAnchor.get(anchor);
    if (existing) {
      return { ...existing, anchorString: anchor };
    }
    return { anchorString: anchor };
  });
};

export const useQuickSignSignatureSettings = ({
  control,
  isOpen,
  setValue,
}: UseQuickSignSignatureSettingsParams) => {
  const { field: fileField } = useController({ control, name: 'stepFour.file' });
  const { field: tabsField } = useController({ control, name: 'stepFour.tabs' });

  const file = getQuickSignStepFourFile(fileField.value);
  const sessionRef = useRef<{ tabs: QuickSignSignatureTab[] }>({ tabs: [] });
  const [docxPreviewBuffer, setDocxPreviewBuffer] = useState<ArrayBuffer | null>(null);

  useEffect(() => {
    if (isOpen) {
      sessionRef.current = { tabs: tabsField.value ?? [] };
    }
  }, [isOpen, tabsField.value]);

  useEffect(() => {
    if (!isOpen || !file) {
      if (!file) {
        setDocxPreviewBuffer(null);
      }
      return;
    }

    let cancelled = false;
    setDocxPreviewBuffer(null);

    void (async () => {
      try {
        const raw = await file.arrayBuffer();
        if (cancelled) return;
        setDocxPreviewBuffer(raw.slice(0));

        const { signatureAnchors: anchors } = await analyzeDocxMergeFields(file, {
          ignoreVariablePlaceholders: true,
        });
        if (cancelled) return;

        setValue('stepFour.tabs', buildTabsFromAnchors(anchors, sessionRef.current.tabs), {
          shouldValidate: true,
        });
      } catch (error) {
        if (!cancelled) {
          setDocxPreviewBuffer(null);
        }
        console.error('DOCX parsing failed', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file, isOpen, setValue]);

  return { docxPreviewBuffer, file, sessionRef };
};
